import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchMakes = createAsyncThunk(
  'vehicle/fetchMakes',
  async (_, { rejectWithValue }) => {
    try {
      // Include both counts; Makes page will use model_count
      const response = await api.get('/makes?with_model_count=true&with_product_count=true');
      // The /makes endpoint returns data in a { makes: [...] } structure
      return response.data.makes || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch makes' });
    }
  }
);

export const fetchModels = createAsyncThunk(
  'vehicle/fetchModels',
  async (makeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/makes/${makeId}/models?with_product_count=true`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch models' });
    }
  }
);

export const fetchSeries = createAsyncThunk(
  'vehicle/fetchSeries',
  async (modelId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/models/${modelId}/series`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch series' });
    }
  }
);

export const fetchBodyTypes = createAsyncThunk(
  'vehicle/fetchBodyTypes',
  async (seriesId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/series/${seriesId}/body-types`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch body types' });
    }
  }
);

export const fetchPositions = createAsyncThunk(
  'vehicle/fetchPositions',
  async ({ bodyTypeId, year }, { rejectWithValue }) => {
    try {
      const params = year ? `?year=${year}` : '';
      const response = await api.get(`/body-types/${bodyTypeId}/positions${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch positions' });
    }
  }
);

export const fetchAllPositions = createAsyncThunk(
  'vehicle/fetchAllPositions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/positions');
      return response.data.positions;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch positions' });
    }
  }
);

// Initial state
const initialState = {
  // Vehicle hierarchy data
  makes: [],
  models: [],
  series: [],
  bodyTypes: [],
  positions: [],
  allPositions: [], // For vehicle diagram
  
  // Current selection
  selectedMake: null,
  selectedModel: null,
  selectedSeries: null,
  selectedBodyType: null,
  selectedPosition: null,
  selectedYear: null,
  
  // Navigation breadcrumb
  breadcrumb: {
    make: null,
    model: null,
    series: null,
    bodyType: null,
  },
  
  // Loading states
  makesLoading: false,
  modelsLoading: false,
  seriesLoading: false,
  bodyTypesLoading: false,
  positionsLoading: false,
  
  // Error state
  error: null,
};

// Vehicle slice
const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedMake: (state, action) => {
      state.selectedMake = action.payload;
      // Clear downstream selections
      state.selectedModel = null;
      state.selectedSeries = null;
      state.selectedBodyType = null;
      state.selectedPosition = null;
      state.models = [];
      state.series = [];
      state.bodyTypes = [];
      state.positions = [];
    },
    setSelectedModel: (state, action) => {
      state.selectedModel = action.payload;
      // Clear downstream selections
      state.selectedSeries = null;
      state.selectedBodyType = null;
      state.selectedPosition = null;
      state.series = [];
      state.bodyTypes = [];
      state.positions = [];
    },
    setSelectedSeries: (state, action) => {
      state.selectedSeries = action.payload;
      // Clear downstream selections
      state.selectedBodyType = null;
      state.selectedPosition = null;
      state.bodyTypes = [];
      state.positions = [];
    },
    setSelectedBodyType: (state, action) => {
      state.selectedBodyType = action.payload;
      // Clear downstream selections
      state.selectedPosition = null;
      state.positions = [];
    },
    setSelectedPosition: (state, action) => {
      state.selectedPosition = action.payload;
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
    setBreadcrumb: (state, action) => {
      state.breadcrumb = { ...state.breadcrumb, ...action.payload };
    },
    clearSelection: (state) => {
      state.selectedMake = null;
      state.selectedModel = null;
      state.selectedSeries = null;
      state.selectedBodyType = null;
      state.selectedPosition = null;
      state.selectedYear = null;
      state.models = [];
      state.series = [];
      state.bodyTypes = [];
      state.positions = [];
      state.breadcrumb = {
        make: null,
        model: null,
        series: null,
        bodyType: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch makes
      .addCase(fetchMakes.pending, (state) => {
        state.makesLoading = true;
        state.error = null;
      })
      .addCase(fetchMakes.fulfilled, (state, action) => {
        state.makesLoading = false;
        state.makes = action.payload;
        state.error = null;
      })
      .addCase(fetchMakes.rejected, (state, action) => {
        state.makesLoading = false;
        state.error = action.payload;
      })
      
      // Fetch models
      .addCase(fetchModels.pending, (state) => {
        state.modelsLoading = true;
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.modelsLoading = false;
        state.models = action.payload.models;
        state.breadcrumb.make = action.payload.make;
        state.error = null;
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.modelsLoading = false;
        state.error = action.payload;
      })
      
      // Fetch series
      .addCase(fetchSeries.pending, (state) => {
        state.seriesLoading = true;
        state.error = null;
      })
      .addCase(fetchSeries.fulfilled, (state, action) => {
        state.seriesLoading = false;
        state.series = action.payload.series;
        state.breadcrumb.make = action.payload.make;
        state.breadcrumb.model = action.payload.model;
        state.error = null;
      })
      .addCase(fetchSeries.rejected, (state, action) => {
        state.seriesLoading = false;
        state.error = action.payload;
      })
      
      // Fetch body types
      .addCase(fetchBodyTypes.pending, (state) => {
        state.bodyTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchBodyTypes.fulfilled, (state, action) => {
        state.bodyTypesLoading = false;
        state.bodyTypes = action.payload.bodyTypes;
        state.breadcrumb.make = action.payload.make;
        state.breadcrumb.model = action.payload.model;
        state.breadcrumb.series = action.payload.series;
        state.error = null;
      })
      .addCase(fetchBodyTypes.rejected, (state, action) => {
        state.bodyTypesLoading = false;
        state.error = action.payload;
      })
      
      // Fetch positions
      .addCase(fetchPositions.pending, (state) => {
        state.positionsLoading = true;
        state.error = null;
      })
      .addCase(fetchPositions.fulfilled, (state, action) => {
        state.positionsLoading = false;
        state.positions = action.payload.positions;
        state.breadcrumb.make = action.payload.make;
        state.breadcrumb.model = action.payload.model;
        state.breadcrumb.series = action.payload.series;
        state.breadcrumb.bodyType = action.payload.bodyType;
        state.error = null;
      })
      .addCase(fetchPositions.rejected, (state, action) => {
        state.positionsLoading = false;
        state.error = action.payload;
      })
      
      // Fetch all positions
      .addCase(fetchAllPositions.fulfilled, (state, action) => {
        state.allPositions = action.payload;
      });
  },
});

export const {
  clearError,
  setSelectedMake,
  setSelectedModel,
  setSelectedSeries,
  setSelectedBodyType,
  setSelectedPosition,
  setSelectedYear,
  setBreadcrumb,
  clearSelection,
} = vehicleSlice.actions;

// Selectors
export const selectMakes = (state) => state.vehicle.makes;
export const selectModels = (state) => state.vehicle.models;
export const selectSeries = (state) => state.vehicle.series;
export const selectBodyTypes = (state) => state.vehicle.bodyTypes;
export const selectPositions = (state) => state.vehicle.positions;
export const selectAllPositions = (state) => state.vehicle.allPositions;

export const selectSelectedMake = (state) => state.vehicle.selectedMake;
export const selectSelectedModel = (state) => state.vehicle.selectedModel;
export const selectSelectedSeries = (state) => state.vehicle.selectedSeries;
export const selectSelectedBodyType = (state) => state.vehicle.selectedBodyType;
export const selectSelectedPosition = (state) => state.vehicle.selectedPosition;
export const selectSelectedYear = (state) => state.vehicle.selectedYear;

export const selectBreadcrumb = (state) => state.vehicle.breadcrumb;

export const selectMakesLoading = (state) => state.vehicle.makesLoading;
export const selectModelsLoading = (state) => state.vehicle.modelsLoading;
export const selectSeriesLoading = (state) => state.vehicle.seriesLoading;
export const selectBodyTypesLoading = (state) => state.vehicle.bodyTypesLoading;
export const selectPositionsLoading = (state) => state.vehicle.positionsLoading;

export const selectVehicleError = (state) => state.vehicle.error;

export default vehicleSlice.reducer;
